import { useEffect, useState } from 'react'
import moment from 'moment'
import { useAppDispatch } from '@/utils/typeHelpers'

// BL
import { ManualTransactionModel } from '@/reducers/admin/allTransactions.slice'
import {
  createSingleManualTransaction,
  validateSingleManualTransaction,
} from '@/features/transactions/transactions.slice'
import { parseErrorFromCatch } from '@/utils/errorHelpers'
import { useIsDeviceWidth, DeviceWidth } from '@/utils/deviceWidthHelpers'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getUserBooksLockedForUserYear } from '@/src/reducers/auth/user.selectors'

import { partitionDuplicates } from './duplicateHelpers'

// UI
import { ScreenData, UploadScreenProps } from './index'
import { Button, Label, Textarea, Text, Input, Dialog, DatePicker } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'
import { LockedBooksMessage } from '@/src/v2-features/transactions/LockedBooksMessage'
import { UPLOAD_COPY } from '@/src/v2-features/transactions/copyConstants'

const UploadSingleTransactionButton = ({
  loading,
  onSaveClicked,
  buttonDisabled,
}: {
  loading: boolean
  onSaveClicked: () => Promise<void>
  buttonDisabled: boolean
}) => (
  <Button
    loading={loading}
    onClick={onSaveClicked}
    disabled={buttonDisabled || loading}
    className="w-full"
  >
    Add Transaction
  </Button>
)

const UploadMultipleTransactionsButton = ({
  goToBulkUploadScreen,
  loading,
}: {
  goToBulkUploadScreen: () => void
  loading: boolean
}) => (
  <div className="block w-full">
    <Text variant={TypographyStyles.BODY_MD} className="text-center">
      {UPLOAD_COPY.LANDING_SCREEN_BULK_SUBTITLE}
    </Text>
    <br />
    <Button
      disabled={loading}
      onClick={goToBulkUploadScreen}
      className="w-full"
    >
      Add Multiple Transactions
    </Button>
  </div>
)

const UploadLandingScreen = ({
  onUploadComplete,
  goToScreenWithData,
  finishFlow,
}: UploadScreenProps) => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [buttonDisabled, setButtonDisabled] = useState(true)
  const [amountInDollars, setAmountInDollars] = useState('0.00')
  const [date, setDate] = useState('')
  const [description, setDescription] = useState('')
  const [serverError, setServerError] = useState<string | null>(null)
  const booksLockedForUserYear = useReselector(getUserBooksLockedForUserYear)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  // Navigates to a specific screen
  const goToBulkUploadScreen = () => goToScreenWithData({ index: 1 })
  const goToDuplicatesScreen = (data: ScreenData) =>
    goToScreenWithData({ index: 3, data })

  // Enable or disable single transaction button
  useEffect(() => {
    const descriptionTrimmed = description.trim()

    if (
      Number(amountInDollars) === 0 ||
      !amountInDollars ||
      !moment(date, DATE_FORMATS.INPUT).isValid() ||
      !descriptionTrimmed
    ) {
      setButtonDisabled(true)
      return
    }

    setButtonDisabled(false)
  }, [amountInDollars, description, date])

  // Invoked when "Add Single Transaction" button is clicked
  const onSaveClicked = async () => {
    setLoading(true)
    const data: ManualTransactionModel = {
      amountInDollars,
      date,
      description: description.trim(),
    }

    try {
      const { errors, warnings } = await validateSingleManualTransaction(data)

      if (errors.length > 0) {
        onUploadComplete?.({ success: false })
        finishFlow()
      } else if (warnings.duplicateGroups.length > 0) {
        const { validTransactions, duplicateGroups } = partitionDuplicates(
          [data],
          warnings.duplicateGroups
        )
        goToDuplicatesScreen({ validTransactions, duplicateGroups })
      } else {
        const result = await createSingleManualTransaction(data)(dispatch)
        data.id = result?.id
        if (result) {
          onUploadComplete?.({
            success: true,
            data: [data],
          })
        } else {
          onUploadComplete?.({ success: false })
        }

        finishFlow()
      }
      setServerError(null)
    } catch (err) {
      setServerError(parseErrorFromCatch(err))
    }
    setLoading(false)
  }

  return (
    <>
      <Dialog.Title>{UPLOAD_COPY.GENERIC_SCREEN_TITLE}</Dialog.Title>
      {!isMobile && (
        <Dialog.Description>
          <Text variant={TypographyStyles.BODY_MD}>
            If you have business transactions from <b>this year </b> that aren’t
            reflected in your connected bank accounts, please add them below.
          </Text>
        </Dialog.Description>
      )}
      <Dialog.Content>
        {booksLockedForUserYear && (
          <div className="w-full">
            <LockedBooksMessage year={booksLockedForUserYear} />
          </div>
        )}
        <div className="flex-1">
          <DatePicker
            fullWidth={isMobile}
            disabled={loading}
            label="Date"
            value={date}
            maxDate={new Date()}
            onChange={(value) => setDate(value)}
            name="transactionDate"
            placeholder={DATE_FORMATS.INPUT}
          />
        </div>
        <div className="flex-1">
          <Label title="Amount">
            <Input
              className={isMobile ? 'w-full' : ''}
              onChange={(e) => setAmountInDollars(e.target.value)}
              value={amountInDollars}
              placeholder="Amount"
              // description="Use negative dollar amounts for expenses (-$10.00) and positive dollar amounts for income ($10.00)"
              type="number"
            />
          </Label>
        </div>
        <div className="flex-1">
          <Label title="Description">
            <Textarea
              disabled={loading}
              placeholder="e.g. USPS Withdrawal"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Label>
        </div>
        {serverError && (
          <Text
            className="server-error"
            variant={TypographyStyles.BODY_MD}
            color={TextColors.DANGER}
          >
            {serverError}
          </Text>
        )}
        <div className="hidden md:flex">
          <UploadSingleTransactionButton
            loading={loading}
            onSaveClicked={onSaveClicked}
            buttonDisabled={buttonDisabled}
          />
        </div>
      </Dialog.Content>
      <Dialog.Footer>
        {isMobile ? (
          <div className="flex flex-col w-full">
            <div>
              <UploadSingleTransactionButton
                loading={loading}
                onSaveClicked={onSaveClicked}
                buttonDisabled={buttonDisabled}
              />
            </div>
            <div>
              <Button
                variant="secondary"
                className="w-full"
                onClick={finishFlow}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <UploadMultipleTransactionsButton
            goToBulkUploadScreen={goToBulkUploadScreen}
            loading={loading}
          />
        )}
      </Dialog.Footer>
    </>
  )
}

export { UploadLandingScreen }
